import {merge} from 'lodash';

import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {SLUG_KEYWORD_ID, URLS_POINTS} from '@axyseo/const/analysis';
import {enhanceResearcherForChinese} from '@axyseo/languageProcessing/helpers/language/chineseHelperFactory';

/**
 * Represents the Slug keyword assessment. This assessment checks if the keyword is present in the slug.
 */
class SlugKeywordAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} config   The configuration to use.
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: SLUG_KEYWORD_ID,
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-on-page-audit/checklist#missing-main-keyword-in-url',
      fixPosition: 'url handle',
      ctaType: 'fix',
      priority: 'medium',
      title: 'Keyword in URL',
      content: {
        good: 'Keyword is included in URL.',
        improve: '',
        bad: 'Missing keyword in URL. Include it for relevance and better SEO.'
      }
    };

    this.identifier = SLUG_KEYWORD_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Executes the Assessment and returns a result.
   *
   * @param {Paper}       paper       The Paper object to assess.
   * @param {Researcher}  researcher  The Researcher object containing all available researches.
   *
   * @returns {AssessmentResult} The result of the assessment, containing both a score and a descriptive text.
   */
  getResult({paper, researcher}) {
    // Enhance researcher for Chinese if needed - specifically for URL context
    const enhancedResearcher = enhanceResearcherForChinese(
      researcher,
      paper.getSlug(),
      paper.getKeyword(),
      true // isUrlContext = true for enhanced Chinese URL matching
    );

    this._keywordInSlug = enhancedResearcher.getResearch('keywordCountInSlug');

    const assessmentResult = new AssessmentResult({config: this._config});

    const calculatedResult = this.calculateResult();

    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);
    return assessmentResult;
  }

  /**
   * Checks whether the paper has a keyword and a slug.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   * @param {Researcher}  researcher  The researcher object.
   *
   * @returns {boolean} True if the paper contains a keyword and a slug, and if the keywordCountInSlug research is available on the researcher.
   */
  isApplicable(paper, researcher) {
    return true;
  }

  /**
   *
   * @returns {{score: number, status: (string)}}
   */
  calculateResult() {
    const status = (() => {
      return this._keywordInSlug.percentWordMatches >= 50 ? 'good' : 'bad';
    })();

    return {
      score: this.getScore(URLS_POINTS, status) || 0,
      status
    };
  }
}

export default SlugKeywordAssessment;
